// React Query hooks for Sentimentals Service
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { sentimentalsService } from "../services/sentimentalsService";
import type {
  SentimentalSettings,
  GetSchedulesParams,
  GetLogsParams,
  TestSendRequest,
  UpcomingOccasion,
} from "../services/sentimentalsService";

// Query keys for sentimentals-related queries
export const sentimentalsQueryKeys = {
  all: ['sentimentals'] as const,
  settings: () => [...sentimentalsQueryKeys.all, 'settings'] as const,
  schedules: (params?: GetSchedulesParams) => [...sentimentalsQueryKeys.all, 'schedules', params] as const,
  logs: (params?: GetLogsParams) => [...sentimentalsQueryKeys.all, 'logs', params] as const,
  upcomingOccasions: (days?: number) => [...sentimentalsQueryKeys.all, 'upcoming-occasions', days] as const,
};

// Hook to get sentimental settings
export const useSentimentalSettings = () => {
  return useQuery({
    queryKey: sentimentalsQueryKeys.settings(),
    queryFn: () => sentimentalsService.getSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to update sentimental settings
export const useUpdateSentimentalSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<SentimentalSettings>) => sentimentalsService.updateSettings(data),
    onSuccess: async (data) => {
      // Invalidate and refetch settings and upcoming occasions for live updates
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: sentimentalsQueryKeys.settings(),
        }),
        queryClient.invalidateQueries({
          queryKey: [...sentimentalsQueryKeys.all, 'upcoming-occasions'],
        }),
      ]);
    },
    onError: (error) => {
      // Error handling is done in the service
      console.error('Failed to update sentimental settings:', error);
    },
  });
};

// Hook to get scheduled messages
export const useSentimentalSchedules = (params: GetSchedulesParams = {}, enabled = true) => {
  return useQuery({
    queryKey: sentimentalsQueryKeys.schedules(params),
    queryFn: () => sentimentalsService.getSchedules(params),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to approve a schedule
export const useApproveSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sentimentalsService.approveSchedule(id),
    onSuccess: async () => {
      // Invalidate all schedule queries to refresh lists
      await queryClient.invalidateQueries({
        queryKey: [...sentimentalsQueryKeys.all, 'schedules'],
      });
    },
    onError: (error) => {
      console.error('Failed to approve schedule:', error);
    },
  });
};

// Hook to reject a schedule
export const useRejectSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sentimentalsService.rejectSchedule(id),
    onSuccess: async () => {
      // Invalidate all schedule queries to refresh lists
      await queryClient.invalidateQueries({
        queryKey: [...sentimentalsQueryKeys.all, 'schedules'],
      });
    },
    onError: (error) => {
      console.error('Failed to reject schedule:', error);
    },
  });
};

// Hook to approve all schedules for a date
export const useApproveAll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (date: string) => sentimentalsService.approveAll(date),
    onSuccess: async () => {
      // Invalidate all schedule queries to refresh lists
      await queryClient.invalidateQueries({
        queryKey: [...sentimentalsQueryKeys.all, 'schedules'],
      });
    },
    onError: (error) => {
      console.error('Failed to approve all schedules:', error);
    },
  });
};

// Hook to get sentimental logs (history)
export const useSentimentalLogs = (params: GetLogsParams = {}, enabled = true) => {
  return useQuery({
    queryKey: sentimentalsQueryKeys.logs(params),
    queryFn: () => sentimentalsService.getLogs(params),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to test send a message
export const useTestSend = () => {
  return useMutation({
    mutationFn: (data: TestSendRequest) => sentimentalsService.testSend(data),
    onError: (error) => {
      console.error('Failed to send test message:', error);
    },
  });
};

// Hook to update a schedule message
export const useUpdateScheduleMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, message }: { scheduleId: string; message: string }) =>
      sentimentalsService.updateScheduleMessage(scheduleId, message),
    onSuccess: async () => {
      // Invalidate all schedule queries to refresh lists
      await queryClient.invalidateQueries({
        queryKey: [...sentimentalsQueryKeys.all, 'schedules'],
      });
    },
    onError: (error) => {
      console.error('Failed to update schedule message:', error);
    },
  });
};

// Hook to get upcoming occasions (birthdays, policy anniversaries, holidays) for personal leads
export const useUpcomingOccasions = (days: number = 60, enabled = true) => {
  return useQuery({
    queryKey: sentimentalsQueryKeys.upcomingOccasions(days),
    queryFn: () => sentimentalsService.getUpcomingOccasions(days),
    enabled,
    staleTime: 30 * 1000, // 30 seconds - keep data fresh
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchInterval: 60 * 1000, // Poll every 60s for upcoming occasions
    refetchIntervalInBackground: false, // Don't poll when tab is not focused (saves resources)
    refetchOnWindowFocus: true, // Refresh when user focuses the tab
  });
};
